import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { handleShowSidescreen } from 'store/ux';
import styles from 'styles/modules/ProfileBio.module.scss';
import SmartText from 'components/SmartText';

import SocialLink from 'components/SocialLink';
import World from 'components/Svg/World';
import AddContent from './AddContent';
import EditContent from './EditContent';
import Expertise from './Expertise';
import Experts from './Experts';
import Typo from 'components/Typo';
import { authSelector } from 'store/auth';
import CreditsDisplay from 'components/CreditsDisplay';
import Star from 'components/Svg/Star';
import Tick from 'components/Svg/Tick';
import Shield from 'components/Svg/Shield';
import SvgDiamond from 'components/Svg/diamond';
import pluralize from 'utils/pluralize';
import { FCA_LABEL, checkedLine, fcaEvidence, hasFca } from 'utils/fca';

const ProfileBio = (props) => {
    const dispatch = useDispatch();
    const [data, setData] = useState(props.data);
    const auth = useSelector(authSelector);

    useEffect(() => {
        setData(props.data);
    }, [props.data]);

    // Raws count for experts; journalists post alerts as part of the job.
    const showRaws = data.rawsCount > 0 && !data.isJournalist;

    const handleEditInfo = () => {
        dispatch(handleShowSidescreen('edit-info'));
    };

    const handleEditDescription = () => {
        dispatch(handleShowSidescreen('edit-description'));
    };

    // Check if credits should be shown (same logic as CreditsDisplay)
    const shouldShowCredits =
        auth.isAuthenticated &&
        data.plan?.type !== 'free' &&
        data.plan?.type !== 'platinum' &&
        !auth.isContributingEditor;

    const badgeConfig = [
        {
            key: 'contributing_editor',
            condition: data.plan?.type === 'contributing_editor',
            Icon: Star,
            label: 'Pro',
            className: styles.badgeEditor,
        },
        {
            key: 'platinum',
            condition: data.plan?.type === 'platinum',
            Icon: SvgDiamond,
            label: 'Platinum',
            className: styles.badgePlatinum,
        },
        {
            key: 'premium',
            condition: data.plan?.type === 'premium',
            Icon: Star,
            label: 'Plus',
            className: styles.badgePremium,
        },
        {
            key: 'fteditor',
            condition: data.isFTEditor,
            Icon: Star,
            label: 'FT Editor',
            className: styles.badgeFTEditor,
        },
        {
            key: 'admin',
            condition: data.isAdmin,
            Icon: Star,
            label: 'Admin',
            className: styles.badgeAdmin,
        },
        {
            key: 'verified',
            condition: data.isVerified,
            Icon: Tick,
            // Tick defaults to #212121, unlike Star which already defaults to white.
            iconProps: { fill: '#fff' },
            label: 'Verified',
            className: styles.badgeVerified,
        },
        {
            key: 'fca',
            condition: !data.isJournalist && hasFca(data.fca),
            Icon: Shield,
            label: FCA_LABEL,
            className: styles.badgeFca,
            title: checkedLine(data.fca),
        },
        {
            key: 'journalist',
            condition: data.isJournalist,
            Icon: Star,
            label: 'Journalist',
            className: styles.badgeJournalist,
        },
    ];

    const badges = badgeConfig
        .filter((badge) => badge.condition)
        .map(({ key, Icon, label, className, iconProps, title }) => (
            <span
                key={key}
                className={[styles.badge, className].join(' ')}
                title={title}
            >
                {Icon && <Icon {...iconProps} />}
                {label}
            </span>
        ));

    const evidence = !data.isJournalist && fcaEvidence(data.fca);

    const about =
        (props.aboutWithCount?.from === data.description &&
            props.aboutWithCount.html) ||
        data.description;

    // A Raws count on its own keeps its place below the employer.
    const claimUnderName = Boolean(props.coverageClaim);
    const claim = (props.coverageClaim || showRaws) && (
        <p
            className={`${styles.coverageClaim} ${
                claimUnderName ? styles.coverageClaimUnderName : ''
            }`}
        >
            {showRaws && props.coverageClaim
                ? `${props.coverageClaim.replace(/\.$/, '')} · `
                : props.coverageClaim}
            {showRaws && (
                <span title="News Alerts the Newspage news team judged strong enough for journalists to run.">
                    {data.rawsCount} {pluralize(data.rawsCount, 'Raw')}
                </span>
            )}
        </p>
    );

    return (
        <section className={styles.bio}>
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    {badges.length > 0 && (
                        <div className={styles.badges}>
                            {badges}
                            {evidence && (
                                <div className={styles.fcaEvidence}>
                                    <p>
                                        <a
                                            href={evidence.href}
                                            target="_blank"
                                            rel="nofollow noopener"
                                        >
                                            FRN {evidence.frn}
                                        </a>
                                        {` · ${evidence.checked}`}
                                    </p>
                                    {evidence.principal && (
                                        <p>{evidence.principal}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <Typo
                        variant={'h1'}
                        Tag={'h1'}
                        font={'serif'}
                        className={styles.title}
                    >
                        {data.name ||
                            (auth.isJournalist === true && 'Your name') ||
                            'Your company name'}
                    </Typo>

                    <EditContent
                        data={{
                            style: 'withLabel',
                            label: 'Edit info',
                            onClick: handleEditInfo,
                        }}
                    />
                </div>

                {claimUnderName && claim}

                {shouldShowCredits && (
                    <div className={styles.creditsSection}>
                        <CreditsDisplay />
                    </div>
                )}
            </header>

            <div className={styles.position}>
                <SmartText
                    quotes={false}
                    height={150}
                    className={styles.smart}
                    change={data.position}
                    data={data.position}
                />
            </div>

            <div className={styles.employer}>
                <SmartText
                    quotes={false}
                    height={150}
                    className={styles.smart}
                    change={data.employer}
                    data={data.employer}
                />
            </div>

            {!claimUnderName && claim}

            <div className={styles.description}>
                {!data.description && (
                    <AddContent
                        data={{
                            direction: 'row',
                            label:
                                (auth.isJournalist === true &&
                                    'Add a short bio. One sentence will do!') ||
                                'Add your company or personal description',
                            onClick: handleEditDescription,
                        }}
                    />
                )}
                <SmartText
                    quotes={false}
                    height={150}
                    className={styles.smart}
                    change={data.description}
                    data={about}
                />
            </div>

            {data.publication && (
                <Typo
                    variant={'h1'}
                    Tag={'h2'}
                    font={'serif'}
                    className={styles.mediaTitle}
                >
                    {data.publication}
                </Typo>
            )}

            <div className={styles.contacts}>
                <div className="row rowVc rowSb">
                    <div className={styles.contactsWebsite}>
                        <div className={styles.contactsIcon}>
                            <World />
                        </div>

                        <div className={styles.contactsOutput}>
                            {data.website && (
                                <Link
                                    href={
                                        data.website.includes('http')
                                            ? data.website
                                            : `https://${data.website}`
                                    }
                                >
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {data.website}
                                    </a>
                                </Link>
                            )}
                        </div>
                    </div>
                    {data.socialNetwork?.length > 0 && (
                        <div className={styles.contactsSocial}>
                            {data.socialNetwork.map((item, key) => {
                                return (
                                    <SocialLink
                                        name={item.name}
                                        url={item.url}
                                        key={`social-link-${key}`}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <div className={['row', 'mt-xl', styles.lists].join(' ')}>
                {/* Keyed so another profile starts with its tags collapsed. */}
                {data.isJournalist === false && <Expertise key={data.slug} />}
            </div>
        </section>
    );
};

export default ProfileBio;
